import React from 'react';
import { connect } from 'react-redux';
import { withStyles } from "tss-react/mui";
import { locationChanged } from 'areas/location/actions';
import { receiveLocationCompetences } from 'areas/supervisor/actions';
import { clear as clearSwipeHistory } from 'areas/swipes/actions';
import withMediaQuery from 'components/WithMediaQuery';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import PromptDialog from 'components/PromptDialog';
import { default as FloatingDialogActions } from 'components/DialogActions';
import ResponsiveDialogHeader from 'components/ResponsiveDialogHeader';
import DialogTransition from 'components/DialogTransition';
import SecondaryDialogButton from 'components/SecondaryDialogButton';
import LocationPanel from '../LocationPanel';
import repository from 'client/repository';
import { mapData } from 'areas/supervisor/domain';
import { processCard } from 'areas/cards/domain';
import { scheduledCompetencies } from 'areas/competence/domain';
import { openScheduledCompetenceDialog } from 'areas/competence/actions';
import { getCardCheckerCard } from 'areas/cards/selectors';
import { updateLocation } from 'workers/mediator';
import isStringSet from 'utilities/is/isStringSet';
import styles from './styles';

class LocationDialog extends React.Component {

    constructor(props) {
        super(props);

        const location = props.location;

        this.state = {

            // not usually recommended, but we want to cache any already loaded division data from the redux state
            // and use this as the basis of the state for this dialog, which will go to the server directly
            // without going through redux actions. Once selected, the state is applied to the store
            divisionsLoading: false,
            divisionDialogOpen: false,
            divisionData: props.divisionData,
            divisions: [],
            divisionDataLoading: false,
            selectedDivision: location.division,
            selectedProject: location.project,
            selectedSite: location.site,
            selectedZone: location.zone,
            isApplying: false,
            locationErrorMessage: '',
            showingLocationError: false
        };
    }

    canLogIntoLocation = (location) => {
        const { userCardUnprocessed, divisionData, config } = this.props;
        const card = processCard(userCardUnprocessed, location, divisionData, config);

        const isBlocked = card.isSuspendedByDivision || card.isSuspendedBySite;
        let message = '';

        if (card.isSuspendedByDivision) {
            message = `A suspension is present in ${config.divisionLevelName.toLowerCase()} ${location.division.divisionName}`;
        }

        if (card.isSuspendedBySite) {
            message = `A suspension is present in ${config.siteLevelName.toLowerCase()} ${location.site.siteName}`;
        }

        if (isBlocked) {
            this.setState({
                showingLocationError: true,
                locationErrorMessage: message
            });

            return false;
        } else {
            this.setState({
                showingLocationError: false,
                locationErrorMessage: ''
            });

            return true;
        }
    };

    onLoadDivisions = async () => {
        let result = {};

        if (this.state.divisions.length === 0) {

            this.setState({ divisionsLoading: true });
            result = await repository.supervisor.getDivisions();
        }

        this.setState({ divisionsLoading: false, divisions: result.divisions || this.state.divisions, divisionDialogOpen: true });
    };

    onCloseDivisionDialog = () => {
        this.setState({ divisionDialogOpen: false });
    };

    componentDidMount = async () => {

        if (!this.props.open) {
            return;
        }
        if (this.state.selectedDivision && this.props.userCard && isStringSet(this.props.userCard.serialNumber)) {

            const data = await repository.supervisor.getDivisionLocations(
                this.props.userCard.serialNumber,
                this.state.selectedDivision.divisionID);

            this.setState({
                divisionDataLoading: false,
                divisionData: {
                    ...mapData(data)
                }
            });
        }
    };

    componentDidUpdate(prevProps) {
        if (prevProps.isApplying !== this.props.isApplying) {
            this.setState({ isApplying: false });
        }
    }

    onDialogLoaded = async () => {

        this.setState({
            selectedProject: null,
            selectedSite: null,
            selectedZone: null
        });

        const data = await repository.supervisor.getDivisionLocations(this.props.userCard.serialNumber, null);

        this.setState({
            divisionDataLoading: false,
            divisionData: {
                ...mapData(data)
            }
        });


    };

    onSiteSelected = (site) => {

        const selectedSite = {
            siteCode: site ? site.siteCode : null,
            siteName: site ? site.name : null
        };

        const projectsToList = [];
        if (this.state.divisionData.projects != null) {
            Object.keys(this.state.divisionData.projects).map((key, index) => (
                Object.keys(this.state.divisionData.projects[key]).map((divisionkey, divisionindex) => (
                    projectsToList.push(this.state.divisionData.projects[key][divisionkey])
                ))
            ));
        }
        const project = projectsToList.find(p => { return p.projectID === site.projectID });
        const selectedProject = {
            projectID: project ? project.projectID : null,
            projectName: project ? project.name : null
        }

        const selectedDivision = {
            divisionID: project ? project.divisionID : null,
            divisionName: project ? project.divisionName : null
        }

        const contenderLocation = this.createLocationData(selectedDivision, selectedProject, selectedSite);
        if (this.canLogIntoLocation(contenderLocation)) {
            this.applyLocation(selectedDivision, selectedProject, selectedSite, null);
        }

    };

    onZoneSelected = (site, zone) => {

        const selectedSite = {
            siteCode: site ? site.siteCode : null,
            siteName: site ? site.name : null
        };

        const projectsToList = [];
        if (this.state.divisionData.projects != null) {
            Object.keys(this.state.divisionData.projects).map((key, index) => (
                Object.keys(this.state.divisionData.projects[key]).map((divisionkey, divisionindex) => (
                    projectsToList.push(this.state.divisionData.projects[key][divisionkey])
                ))
            ));
        }
        const project = projectsToList.find(p => { return p.projectID === site.projectID });
        const selectedProject = {
            projectID: project ? project.projectID : null,
            projectName: project ? project.name : null
        }

        const selectedDivision = {
            divisionID: project ? project.divisionID : null,
            divisionName: project ? project.divisionName : null
        }

        const selectedZone = {
            zoneCode: zone ? zone.zoneCode : null,
            zoneName: zone ? zone.name : null
        }

        const contenderLocation = this.createLocationData(selectedDivision, selectedProject, selectedSite);
        if (this.canLogIntoLocation(contenderLocation)) {
            this.applyLocation(selectedDivision, selectedProject, selectedSite, selectedZone);
        }
    };

    createLocationData(divisionData, projectData, siteData, zoneData) {
        return {
            division: divisionData,
            project: projectData,
            site: siteData,
            zone: zoneData ? { zoneCode: zoneData.zoneCode } : null
        };
    }

    applyLocation = async (selectedDivision, selectedProject, selectedSite, selectedZone) => {

        const { userCard } = this.props;

        this.setState({ isApplying: true });

        updateLocation(this.createLocationData(selectedDivision, selectedProject, selectedSite, selectedZone));

        const competenceData = await repository.supervisor.getCompetences(
            this.props.userCard.serialNumber,
            selectedDivision.divisionID,
            selectedProject.projectID,
            selectedSite.siteCode,
            (selectedZone ? selectedZone.zoneCode : null));

        this.props.dispatch(
            locationChanged(
                selectedDivision.divisionID,
                selectedDivision.divisionName,
                selectedProject.projectID,
                selectedProject.projectName,
                selectedSite.siteCode,
                selectedSite.siteName,
                selectedZone ? selectedZone.zoneCode : null,
                selectedZone ? selectedZone.zoneName : null));

        this.props.dispatch(receiveLocationCompetences(competenceData));

        const siteCompetencies = scheduledCompetencies(competenceData, { site: selectedSite });
        if (siteCompetencies.length > 0) {
            if (userCard && userCard.canSwipeCards) {
                this.props.dispatch(openScheduledCompetenceDialog());
            }
        }

        try {
            if (this.props.location.division.divisionID !== selectedDivision.divisionID ||
                this.props.location.project.projectID !== selectedProject.projectID ||
                this.props.location.site.siteCode !== selectedSite.siteCode) {
                this.props.dispatch(clearSwipeHistory());
            }
        } catch (e) {
        }

        this.props.handleClose();
    };

    canSave() {
        const {
            selectedDivision,
            selectedProject,
            selectedSite
        } = this.state;

        return (selectedDivision && isStringSet(selectedDivision.divisionID))
            && (selectedProject && isStringSet(selectedProject.projectID))
            && (selectedSite && isStringSet(selectedSite.siteCode));
    }

    onClosePrompt = () => {
        this.setState({ showingLocationError: false, locationErrorMessage: '' });
    };

    render() {
        const { classes, fullScreen } = this.props;

        const { isApplying, divisions, showingLocationError, locationErrorMessage } = this.state;
        return (
            <React.Fragment>
                <PromptDialog
                    open={!!showingLocationError}
                    message={locationErrorMessage}
                    confirmClicked={this.onClosePrompt}
                    confirmText="Close"
                    showCancel={false} />

                <Dialog
                    fullScreen={fullScreen}
                    open={this.props.open}
                    onClose={this.props.handleClose}
                    TransitionComponent={DialogTransition}
                    TransitionProps={{
                        onExiting: this.handleClose
                    }}>

                    <ResponsiveDialogHeader
                        fullScreen={fullScreen}
                        title={this.props.title || "Location"}
                        showBackIcon={false}
                        disableClose={!this.props.canCancel}
                        handleClose={this.props.handleClose} />

                    <DialogContent className={classes.contentRoot}>
                        <LocationPanel
                            divisionDialogOpen={this.state.divisionDialogOpen}
                            onCloseDivisionDialog={this.onCloseDivisionDialog}
                            onLoadDivisions={this.onLoadDivisions}
                            divisionsLoading={this.state.divisionsLoading}
                            divisionDataLoading={this.state.divisionDataLoading}
                            divisions={divisions || []}
                            projects={this.state.divisionData.projects}
                            sites={this.state.divisionData.sites}
                            zones={this.state.divisionData.zones}

                            divisionName={this.props.divisionName}
                            projectName={this.props.projectName}
                            siteName={this.props.siteName}
                            zoneName={this.props.zoneName}

                            selectedDivision={this.state.selectedDivision}
                            selectedProject={this.state.selectedProject}
                            selectedSite={this.state.selectedSite}
                            selectedZone={this.state.selectedZone}

                            onDialogLoaded={this.onDialogLoaded}
                            onSiteSelected={this.onSiteSelected}
                            onZoneSelected={this.onZoneSelected}
                        />
                    </DialogContent>
                    {!fullScreen &&
                        <FloatingDialogActions fullScreen={fullScreen}>
                            {this.props.canCancel &&
                                <SecondaryDialogButton onClick={this.props.handleClose}>
                                    Cancel
                                </SecondaryDialogButton>
                            }
                        </FloatingDialogActions>
                    }
                </Dialog>
            </React.Fragment>
        );
    }
}

function mapStateToProps(state) {
    const { cards, supervisor, config, location } = state;

    const cardCheckerCardQrCode = repository.auth.getQRCode();
    const cardCheckerCard = cardCheckerCardQrCode ? cards.cards[cardCheckerCardQrCode] : null;

    const userCard = getCardCheckerCard(state);

    return {
        userCardUnprocessed: cardCheckerCard,
        userCard: userCard,
        divisionData: supervisor || {},
        divisionName: config.hierarchyLevelNameToSelectAtLogin,
        projectName: config.projectLevelName,
        siteName: config.siteLevelName,
        zoneName: config.zoneLevelName,
        location: location,
        config: config,
        isApplying: supervisor.isApplying
    };
}

const Media = withMediaQuery(theme => theme.breakpoints.down('lg'))(LocationDialog);
const styled = withStyles(Media, styles);
const ConnectedPage = connect(mapStateToProps)(styled);
export default ConnectedPage;
